<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import { toolkitUrls } from '@/util/links'
import { getUpcomingWebinars, type Webinar } from './api'
import { FROM_HOME, HOME_EVENT_NAMES } from './event_names'
import { TRAINING_CENTER_HEADING_CLASSES, TRAINING_CENTER_LINK_CLASSES } from './training_center_styles'
import TrainingCenterLoadingNotice from './TrainingCenterLoadingNotice.vue'

const analytics = getAnalyticsTracker()
const webinars = ref<Webinar[]>([])
const isLoading = ref(true)
const hasWebinars = computed(() => webinars.value.length > 0)

async function loadWebinars() {
  try {
    webinars.value = await getUpcomingWebinars()
  } catch (error) {
    console.error('Error fetching webinars', error)
  } finally {
    isLoading.value = false
  }
}

function trackWebinarClick(webinar: Webinar, index: number) {
  analytics.sendEvent(HOME_EVENT_NAMES.CLICKED_UPCOMING_WEBINAR, {
    ...FROM_HOME,
    title: webinar.title,
    date: webinar.date,
    position: index + 1
  })
}

function trackViewAllWebinars() {
  analytics.sendEvent(HOME_EVENT_NAMES.CLICKED_VIEW_ALL_WEBINARS, FROM_HOME)
}

onMounted(loadWebinars)
</script>

<template>
  <div class="ml-4">
    <h3 :class="TRAINING_CENTER_HEADING_CLASSES" class="mt-[14px] md:max-xl:mt-0">Upcoming Webinars</h3>
    <TrainingCenterLoadingNotice v-if="isLoading">Loading upcoming webinars...</TrainingCenterLoadingNotice>
    <ul v-if="hasWebinars" class="mb-[10px] list-none p-0">
      <li
        v-for="(webinar, index) in webinars"
        :key="webinar.url"
        class="mb-[30px] flex flex-col last:mb-4"
        data-testid="webinar"
      >
        <a
          :href="webinar.url"
          :class="TRAINING_CENTER_LINK_CLASSES"
          class="text-[1rem] font-medium"
          rel="noopener"
          target="_blank"
          @click="trackWebinarClick(webinar, index)"
        >
          <span class="sr-only">Webinar:</span>
          <span>{{ webinar.title }}</span>
          <span class="sr-only">Date: {{ webinar.date }}</span>
          <span class="sr-only">(opens in a new tab)</span>
        </a>
        <span>{{ webinar.date }}</span>
      </li>
    </ul>
    <ul v-else class="mb-[10px] list-none p-0">
      <li class="mb-4">No upcoming webinars</li>
    </ul>
    <ul class="mb-[10px] list-none p-0">
      <li class="mb-2">
        <a
          :href="toolkitUrls.webinars"
          :class="TRAINING_CENTER_LINK_CLASSES"
          rel="noopener"
          target="_blank"
          data-testid="view-all-webinars"
          @click="trackViewAllWebinars"
        >
          View all upcoming webinars
          <span class="sr-only">(opens in a new tab)</span>
        </a>
      </li>
      <li class="mb-2">
        <a
          :href="toolkitUrls.pastWebinars"
          :class="TRAINING_CENTER_LINK_CLASSES"
          rel="noopener"
          target="_blank"
          data-testid="past-webinars"
        >
          View past webinar recordings
          <span class="sr-only">(opens in a new tab)</span>
        </a>
      </li>
    </ul>
  </div>
</template>
